/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit, Mail, Phone, Building2, Shield, Loader2, Save, Check, Star, Send, Award, X, UserPlus, Search, Upload, FileText, Trash2, Eye, Download, Printer, FileCheck, ScrollText, ShieldCheck, Calendar, Hash, MapPin, Globe, Users, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../../lib/utils';
import { formatCodeNAF } from '../../utils/codeNaf';

const API = process.env.REACT_APP_BACKEND_URL;

export const RemiseSolidaireModal = ({ membre, open, onClose, onSave, token }) => {
  const [pct, setPct] = useState('');
  const [eur, setEur] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null); // { set_at, set_by }

  // Charge la config actuelle à l'ouverture
  useEffect(() => {
    if (!open || !membre?.id || !token) return;
    setLoading(true);
    axios
      .get(`${API}/api/admin/members/${membre.id}/remise-solidaire`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const d = res.data || {};
        setPct(d.remise_solidaire_max_pct > 0 ? String(d.remise_solidaire_max_pct) : '');
        setEur(d.remise_solidaire_max_eur > 0 ? String(d.remise_solidaire_max_eur) : '');
        setComment(d.comment || '');
        setMeta({
          set_at: d.set_at,
          set_by: d.set_by,
          enabled: d.enabled,
          annexe_numero: d.annexe_numero,
          annexe_doc_id: d.annexe_doc_id,
        });
      })
      .catch(() => {
        setPct('');
        setEur('');
        setComment('');
        setMeta(null);
      })
      .finally(() => setLoading(false));
  }, [open, membre?.id, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        remise_solidaire_max_pct: pct ? parseFloat(pct) : 0,
        remise_solidaire_max_eur: eur ? parseFloat(eur) : 0,
        comment: comment.trim() || null,
      };
      const res = await axios.put(
        `${API}/api/admin/members/${membre.id}/remise-solidaire`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.enabled) {
        const parts = [];
        if (res.data.remise_solidaire_max_pct > 0) parts.push(`-${res.data.remise_solidaire_max_pct}%`);
        if (res.data.remise_solidaire_max_eur > 0) parts.push(`-${res.data.remise_solidaire_max_eur} €`);
        toast.success(`Remise solidaire configurée : ${parts.join(' et ')}`);
      } else {
        toast.info('Remise solidaire supprimée');
      }
      if (onSave) onSave(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Supprimer la remise solidaire négociée pour ce membre ?')) return;
    setPct('');
    setEur('');
    setComment('');
    // Sauvegarde immédiate avec 0/0 (le backend supprimera les champs)
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/admin/members/${membre.id}/remise-solidaire`,
        { remise_solidaire_max_pct: 0, remise_solidaire_max_eur: 0, comment: null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.info('Remise solidaire supprimée');
      if (onSave) onSave(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  if (!membre) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="remise-solidaire-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-emerald-600" />
            Remise solidaire négociée
          </DialogTitle>
          <DialogDescription>
            Pré-paramétrer une remise commerciale pour <strong>{membre.prenom} {membre.nom}</strong>{' '}
            ({membre.raison_sociale || membre.entreprise || '—'}). Le simulateur BATICO l'appliquera
            automatiquement à chaque simulation.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="remise-pct" className="text-xs font-semibold text-emerald-800 mb-1 block">
                  Remise maximum (%)
                </Label>
                <Input
                  id="remise-pct"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  placeholder="Ex: 10"
                  value={pct}
                  onChange={(e) => setPct(e.target.value)}
                  className="border-emerald-300 focus:border-emerald-500 h-9"
                  data-testid="input-remise-solidaire-pct"
                />
                <p className="text-[10px] text-slate-500 mt-1">0 = aucune remise en %</p>
              </div>
              <div>
                <Label htmlFor="remise-eur" className="text-xs font-semibold text-emerald-800 mb-1 block">
                  Remise maximum (€)
                </Label>
                <Input
                  id="remise-eur"
                  type="number"
                  min="0"
                  step="10"
                  placeholder="Ex: 100"
                  value={eur}
                  onChange={(e) => setEur(e.target.value)}
                  className="border-emerald-300 focus:border-emerald-500 h-9"
                  data-testid="input-remise-solidaire-eur"
                />
                <p className="text-[10px] text-slate-500 mt-1">0 = aucune remise en €</p>
              </div>
            </div>

            <div>
              <Label htmlFor="remise-comment" className="text-xs font-semibold text-slate-700 mb-1 block">
                Note interne (optionnel)
              </Label>
              <Input
                id="remise-comment"
                type="text"
                placeholder="Ex: Partenariat commercial 2026, négociation directe…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="h-9"
                data-testid="input-remise-solidaire-comment"
              />
            </div>

            {meta?.set_at && (
              <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 space-y-1">
                <div>
                  Dernière mise à jour : {new Date(meta.set_at).toLocaleString('fr-FR')}
                  {meta.set_by && <> par <strong>{meta.set_by}</strong></>}
                </div>
                {meta.annexe_numero && (
                  <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
                    <FileText className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    <span>
                      Annexe générée :{' '}
                      <strong className="text-emerald-700">{meta.annexe_numero}</strong>
                    </span>
                    {meta.annexe_doc_id && (
                      <a
                        href={`${API}/api/admin/membres/${membre.id}/documents/${meta.annexe_doc_id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto text-emerald-700 font-semibold underline hover:text-emerald-900"
                        data-testid="open-annexe-pdf-link"
                      >
                        Télécharger le PDF →
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
              <p className="font-semibold mb-1">💡 Comment ça marche ?</p>
              <ul className="list-disc list-inside space-y-0.5 text-emerald-800">
                <li>Les deux remises sont <strong>cumulables</strong> (% puis €).</li>
                <li>Le simulateur active <strong>automatiquement</strong> le panneau « Personnalisation des montants » et pré-remplit les champs.</li>
                <li>Un Super Admin pourra toujours ajuster les valeurs au moment de la simulation.</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter className="pt-2 flex-wrap gap-2">
          {meta?.enabled && (
            <Button
              variant="outline"
              onClick={handleClear}
              disabled={saving || loading}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 mr-auto"
              data-testid="clear-remise-solidaire-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer la remise
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-remise-solidaire-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
